import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Col, Button, Dropdown, Form, InputGroup } from 'react-bootstrap'
import { selectArtForms, selectTags } from 'store/settingsSlice'
import * as c from 'assets/constants'
import { TargetGroups } from 'assets/data'
import { useNavigate, useSearchParams } from 'react-router-dom'
import * as routes from 'assets/routes'

export interface ISearchFormProps {
  setBackgroundTransparentMobile?: boolean
  callback?: (
    searchString: string,
    artform: string,
    targetGroup: string,
    tags: string[],
    searchCreativeSchool: boolean,
  ) => void
  onSearchPerformed?: () => void
  performNavigation?: boolean
}

export const SearchForm = (props: ISearchFormProps) => {
  const [searchParams] = useSearchParams()
  const { setBackgroundTransparentMobile, callback, performNavigation, onSearchPerformed } = props
  const [searchCreativeSchool, setSearchCreativeSchool] = useState<boolean>(
    searchParams.get('creativeSchool') === 'true',
  )
  const [selectedArtForm, setSelectedArtForm] = useState<string | null>((searchParams.get('artform') as string) ?? null)
  const [selectedTargetGroup, setSelectedTargetGroup] = useState<string | null>(
    (searchParams.get('targetGroup') as string) || null,
  )
  const [selectedTags, setSelectedTags] = useState<string[]>(
    searchParams.get('tags') ? searchParams.get('tags')!.split(',').filter(Boolean) : [],
  )
  const [searchString, setSearchString] = useState<string | null>((searchParams.get('searchString') as string) ?? null)
  const artForms = useSelector(selectArtForms)
  const tags = useSelector(selectTags)
  const navigate = useNavigate()

  const submitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (callback) {
      callback(searchString || '', selectedArtForm || '', selectedTargetGroup || '', selectedTags, searchCreativeSchool)

      if (!performNavigation) return
    }

    const url = `${routes.SEARCH}`
    let searchParamsString = searchString ? `?searchString=${searchString}` : '?searchString=*'

    if (selectedArtForm) searchParamsString += `${searchParamsString.length > 0 ? '&' : '?'}artform=${selectedArtForm}`
    if (selectedTargetGroup)
      searchParamsString += `${searchParamsString.length > 0 ? '&' : '?'}targetGroup=${selectedTargetGroup}`
    if (selectedTags.length > 0)
      searchParamsString += `${searchParamsString.length > 0 ? '&' : '?'}tags=${selectedTags.join(',')}`
    if (searchCreativeSchool) searchParamsString += `${searchParamsString.length > 0 ? '&' : '?'}creativeSchool=true`

    navigate(`${url}${searchParamsString}`)

    if (onSearchPerformed) onSearchPerformed()
  }

  return (
    <>
      <div
        className={`container-narrow mt-auto bg-md-transparent ${setBackgroundTransparentMobile ? '' : 'bg-theme-4'}`}
      >
        <div className="d-inline-block bg-md-white rounded-top px-md-4 pt-3">
          <span className="mb-1 h5">{c.HOME_SEARCH_TITLE}</span>
        </div>
        <div className={`bg-md-white px-md-4 mb-md-4 rounded-end rounded-bottom pt-md-3 pb-1`}>
          <Form className="row" onSubmit={(event) => submitForm(event)}>
            <Form.Group className="col-12 col-md-3 mb-2 mb-md-0" controlId="filter-artform">
              <span className="label mb-1 fw-bold">{c.HOME_SEARCH_CHOOSE_ARTFORM}</span>
              <Dropdown>
                <Dropdown.Toggle variant="primary" className="w-100 d-flex justify-content-between align-items-center">
                  {selectedArtForm ? selectedArtForm : 'Alla'}
                </Dropdown.Toggle>
                {artForms && (
                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={() => {
                        setSelectedArtForm('')
                      }}
                    >
                      Alla
                    </Dropdown.Item>
                    {artForms?.map((artform, index) => (
                      <Dropdown.Item
                        key={`artform-${index}`}
                        onClick={() => {
                          setSelectedArtForm(artform)
                        }}
                        className={`border-1 border-top`}
                      >
                        {artform}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                )}
              </Dropdown>
            </Form.Group>
            <Form.Group className="col-12 col-md-3 mb-2 mb-md-0" controlId="filter-target-group">
              <span className="label mb-1 fw-bold">{c.HOME_SEARCH_CHOOSE_TARGET_GROUP}</span>
              <Dropdown>
                <Dropdown.Toggle
                  variant="secondary"
                  className="w-100 d-flex justify-content-between align-items-center"
                >
                  {selectedTargetGroup ? selectedTargetGroup : 'Alla'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => {
                      setSelectedTargetGroup('')
                    }}
                  >
                    Alla
                  </Dropdown.Item>
                  {TargetGroups.map((targetGroup, index) => (
                    <Dropdown.Item
                      key={`target-group-${index}`}
                      className={`border-1 border-top`}
                      onClick={() => setSelectedTargetGroup(targetGroup)}
                    >
                      {targetGroup}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>
            <Form.Group className="col-12 col-md-3 mb-2 mb-md-0" controlId="filter-tag">
              <span className="label mb-1 fw-bold">{c.HOME_SEARCH_CHOOSE_TAG}</span>
              <Dropdown autoClose="outside">
                <Dropdown.Toggle
                  variant="secondary"
                  className="w-100 d-flex justify-content-between align-items-center"
                >
                  {selectedTags.length === 0
                    ? 'Alla'
                    : selectedTags.length === 1
                    ? selectedTags[0]
                    : `Flera (${selectedTags.length})`}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setSelectedTags([])} active={selectedTags.length === 0}>
                    Alla
                  </Dropdown.Item>
                  {tags?.map((tag, index) => (
                    <Dropdown.Item
                      key={`tag-${index}`}
                      className="border-1 border-top"
                      onClick={() => {
                        setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]))
                      }}
                    >
                      <Form.Check
                        type="checkbox"
                        label={tag}
                        checked={selectedTags.includes(tag)}
                        onChange={() => {}}
                        onClick={(e) => e.stopPropagation()}
                        className="pe-none"
                      />
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>
            <Form.Group
              className="col-12 col-md-3 mb-2 mb-md-0 d-flex align-items-end"
              controlId="filter-creative-school"
            >
              <Form.Check
                type="checkbox"
                id="filter-workshop"
                label="Workshop"
                checked={searchCreativeSchool}
                onChange={(e) => setSearchCreativeSchool(e.target.checked)}
              />
            </Form.Group>
            <Col xs={12} className="mt-md-2">
              <InputGroup className="mb-3">
                <Form.Control
                  id="search-field"
                  type="text"
                  value={!searchString || searchString === '*' ? '' : (searchString as string)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSearchString(e.target.value)
                  }}
                  className="border-0 bg-white bg-md-light rounded-start"
                  placeholder="Sök här..."
                  aria-label={c.SEARCH}
                />
                <Button variant="primary" type="submit" className="w-md-auto rounded-end-pill rounded-end-md-2">
                  {c.SEARCH}
                </Button>
              </InputGroup>
            </Col>
          </Form>
        </div>
      </div>
    </>
  )
}
